import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, ArrowRight, Play } from "lucide-react";
import { useLanguage, waLink } from "@/i18n";
import { products } from "@/data/products";

export default function Catalog() {
  const { lang, t } = useLanguage();
  const [filter, setFilter] = useState("all");
  const filters = ["all", "balok", "papan", "reng", "outdoor"];
  const visible = useMemo(
    () => (filter === "all" ? products : products.filter((p) => p.cat === filter)),
    [filter]
  );

  const onSpot = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <section id="katalog" className="py-24 sm:py-32 bg-[#0B1220] blueprint-grid" data-testid="catalog-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6"
        >
          <div className="max-w-xl">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-sky-400 font-semibold">{t.catalog.eyebrow}</p>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-white text-2xl sm:text-3xl lg:text-4xl">{t.catalog.title}</h2>
            <p className="mt-4 text-slate-400 text-base sm:text-lg">{t.catalog.sub}</p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="catalog-filters">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`catalog-filter-${f}`}
                className={`px-4 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all ${
                  filter === f
                    ? "bg-sky-500 text-white shadow-lg"
                    : "bg-[#101B2E] text-slate-300 border border-slate-700 hover:border-sky-500 hover:text-sky-400"
                }`}
              >
                {t.catalog.filters[f]}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div layout className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.article
                layout
                key={p.id}
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                onMouseMove={onSpot}
                data-testid={`product-card-${p.id}`}
                className="spotlight-card group bg-[#101B2E] rounded-2xl border border-slate-800 overflow-hidden hover:border-sky-500/60 transition-colors duration-300 hover:shadow-[0_30px_60px_-24px_rgba(14,116,144,0.45)]"
              >
                <Link to={`/produk/${p.id}`} data-testid={`product-link-${p.id}`} className="block relative overflow-hidden aspect-[4/3] clip-industrial">
                  <img
                    src={p.img}
                    alt={`${p.nameId} — ${p.dim}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute top-3 left-3 bg-emerald-500/95 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                    {t.catalog.ready}
                  </span>
                  <span className="absolute top-3 right-3 bg-[#070E1A]/85 text-sky-300 font-mono text-[10px] tracking-widest px-3 py-1.5 rounded-full">
                    #{String(p.id).padStart(2, "0")}
                  </span>
                  {p.video && (
                    <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full" data-testid={`video-badge-${p.id}`}>
                      <Play size={11} className="fill-current" /> Video
                    </span>
                  )}
                </Link>
                <div className="p-6">
                  <Link to={`/produk/${p.id}`}>
                    <h3 className="font-display font-bold text-base sm:text-lg text-slate-100 leading-snug group-hover:text-sky-400 transition-colors">
                      {lang === "id" ? p.nameId : p.nameEn}
                    </h3>
                  </Link>
                  <dl className="mt-4 space-y-1.5 text-sm">
                    <div className="flex justify-between gap-3">
                      <dt className="text-slate-400">{t.catalog.labels.size}</dt>
                      <dd className="font-semibold text-slate-200 text-right">{p.dim}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-slate-400">{t.catalog.labels.grade}</dt>
                      <dd className="font-semibold text-wood-cedar text-right">{p.grade}</dd>
                    </div>
                  </dl>
                  <div className="mt-5 flex gap-2">
                    <a
                      href={waLink(t.catalog.orderMsg(lang === "id" ? p.nameId : p.nameEn, p.dim))}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`wa-order-${p.id}`}
                      className="inline-flex flex-1 items-center justify-center gap-2 bg-sky-500 hover:bg-sky-400 text-white text-sm font-semibold px-4 py-3 rounded-xl transition-colors"
                    >
                      <MessageCircle size={16} /> {t.catalog.order}
                    </a>
                    <Link
                      to={`/produk/${p.id}`}
                      data-testid={`product-detail-${p.id}`}
                      aria-label={t.catalog.detail}
                      className="inline-flex items-center justify-center w-12 rounded-xl border-2 border-slate-700 text-slate-400 hover:border-sky-500 hover:text-sky-400 transition-colors"
                    >
                      <ArrowRight size={17} />
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
